import React, { useState, useRef, useEffect } from 'react';
import {
  FiBell,
  FiX,
  FiPackage,
  FiMessageCircle,
  FiCheckCircle,
  FiClock,
  FiArrowLeft,
} from 'react-icons/fi';
import { useNotificaciones } from '../context/NotificacionesContext';
import { useNavigate } from 'react-router-dom';

const iconoTipo = {
  mensaje: <FiMessageCircle size={14} color="var(--color-secondary)" />,
  pedido: <FiPackage size={14} color="#fbbc04" />,
  fase: <FiClock size={14} color="#673ab7" />,
  confirmacion: <FiCheckCircle size={14} color="var(--color-success)" />,
};

const CampanaNotificaciones = ({ style, onVerPedido }) => {
  const { notificaciones, noLeidas, marcarTodasLeidas } = useNotificaciones();
  const [abierto, setAbierto] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setAbierto(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const abrirNotificacion = (n) => {
    setAbierto(false);
    if (n.pedidoId) {
      if (onVerPedido) onVerPedido(n.pedidoId);
    } else if (n.url) {
      navigate(n.url);
    }
  };

  const recientes = notificaciones.slice(0, 10);

  const contenido = (
    <>
      <div style={S.panelHeader}>
        {isMobile && (
          <button onClick={() => setAbierto(false)} style={S.backBtn}>
            <FiArrowLeft size={20} />
          </button>
        )}
        <span style={S.panelTitle}>Notificaciones</span>
        {!isMobile && (
          <button onClick={() => setAbierto(false)} style={S.closeBtn}>
            <FiX size={16} />
          </button>
        )}
      </div>
      <div style={S.panelBody}>
        {recientes.length === 0 && <div style={S.emptyState}>Sin notificaciones</div>}
        {recientes.map((n) => (
          <div key={n.id} onClick={() => abrirNotificacion(n)} style={S.notifItem}>
            <div style={{ paddingTop: '2px' }}>{iconoTipo[n.tipo] || <FiBell size={14} />}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.notifTitulo}>{n.titulo}</div>
              <div style={S.notifMensaje}>{n.mensaje}</div>
              <div style={S.notifHora}>
                {new Date(n.timestamp).toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        onClick={() => {
          setAbierto(!abierto);
          if (!abierto) marcarTodasLeidas();
        }}
        style={S.bellBtn}
      >
        <FiBell size={20} />
        {noLeidas > 0 && <span style={S.badge}>{noLeidas > 9 ? '9+' : noLeidas}</span>}
      </button>

      {abierto && isMobile && (
        <div style={S.mobileOverlay}>
          <div style={S.mobilePanel}>{contenido}</div>
        </div>
      )}

      {abierto && !isMobile && <div style={S.desktopDropdown}>{contenido}</div>}
    </div>
  );
};

const S = {
  bellBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    position: 'relative',
    padding: '6px 8px',
    display: 'flex',
    alignItems: 'center',
    color: 'inherit',
  },
  badge: {
    position: 'absolute',
    top: '2px',
    right: '2px',
    backgroundColor: 'var(--color-danger)',
    color: 'var(--color-surface)',
    fontSize: '10px',
    fontWeight: 700,
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 4px',
  },
  panelHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--color-border)',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flexShrink: 0,
  },
  panelTitle: { fontSize: '16px', fontWeight: 700, flex: 1 },
  backBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-primary)',
    padding: '4px',
    display: 'flex',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--color-text-secondary)',
    padding: '2px',
  },
  panelBody: { flex: 1, overflowY: 'auto' },
  emptyState: { padding: '40px 16px', textAlign: 'center', color: '#9aa0a6', fontSize: '13px' },
  notifItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: '1px solid var(--color-bg)',
    display: 'flex',
    gap: '10px',
    alignItems: 'flex-start',
    transition: 'background 0.15s',
  },
  notifTitulo: { fontSize: '13px', fontWeight: 600, color: 'var(--color-primary)' },
  notifMensaje: { fontSize: '12px', color: 'var(--color-text-secondary)', marginTop: '2px' },
  notifHora: { fontSize: '10px', color: '#9aa0a6', marginTop: '4px' },
  desktopDropdown: {
    position: 'absolute',
    top: '42px',
    right: '0',
    width: '340px',
    maxHeight: '420px',
    backgroundColor: 'var(--color-surface)',
    borderRadius: '12px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    overflow: 'hidden',
    zIndex: 1000,
    display: 'flex',
    flexDirection: 'column',
  },
  mobileOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
  },
  mobilePanel: {
    backgroundColor: 'var(--color-surface)',
    borderRadius: '20px 20px 0 0',
    width: '100%',
    maxWidth: '900px',
    height: '85vh',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
    overflow: 'hidden',
  },
};

export default CampanaNotificaciones;
